import { useEffect, useState } from "react";
import { Loader2, Download } from "lucide-react";
import { fetchBlobUrl } from "@/lib/download";
import { Button } from "@/components/ui-kit/Button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui-kit/Dialog";

/**
 * Inline preview for an authenticated server-generated PDF. Fetches the file as
 * a blob (so the bearer token is attached) and renders it in an iframe.
 */
export function PdfPreviewDialog({ open, onOpenChange, path, params = {}, title = "Document preview" }) {
  const [url, setUrl] = useState("");
  const [filename, setFilename] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const key = JSON.stringify(params);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    let objectUrl = "";
    const load = async () => {
      setLoading(true);
      setError("");
      setUrl("");
      try {
        const res = await fetchBlobUrl(path, params);
        if (!active) {
          URL.revokeObjectURL(res.url);
          return;
        }
        objectUrl = res.url;
        setFilename(res.filename);
        setUrl(res.url);
      } catch (e) {
        if (active) setError(e.response?.statusText || e.message || "Failed to load preview.");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, path, key]);

  const download = () => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl overflow-hidden p-0" data-testid="pdf-preview-dialog">
        <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
          <DialogTitle className="truncate text-base">{filename || title}</DialogTitle>
          <Button size="sm" variant="secondary" onClick={download} disabled={!url} data-testid="pdf-preview-download" className="mr-8">
            <Download className="h-4 w-4" /> Download
          </Button>
        </div>
        <div className="h-[80vh] bg-secondary/30">
          {loading && (
            <div className="grid h-full place-items-center" data-testid="pdf-preview-loading">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}
          {error && !loading && (
            <div className="grid h-full place-items-center px-6 text-center text-sm text-destructive" data-testid="pdf-preview-error">
              {error}
            </div>
          )}
          {url && !loading && (
            <iframe title="PDF preview" src={url} className="h-full w-full border-0" data-testid="pdf-preview-frame" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
